/**
 * Every string `/content/:contentItemId/edit` renders.
 *
 * The surface **shows** the state and does not change it. What left with the
 * transitions is not replaced by nothing: a link back to the list stands in
 * their place, so the page names its own successor rather than dead-ending.
 */
export const copy = {
  head: {
    /**
     * The lede names the shape and points at where its parts are managed. It
     * counts **nothing**: the season and episode figures live on the list and on
     * the assets surface, and this read carries neither — a count rendered from
     * a payload that does not hold one would be a zero the operator could not
     * check.
     */
    ledeSeries: 'مسلسل. هنا تُحرَّر حقول العنوان؛ المواسم والحلقات وملفّاتها في صفحة الأصول.',
    ledeFilm: 'فيلم. هنا تُحرَّر حقول العنوان؛ الملفّ وترجماته في صفحة الأصول.',
  },

  state: {
    heading: 'الحالة',
    changeFromList: 'غيّر الحالة من القائمة',
    current: 'الحالة الحاليّة',
    publishedAt: 'تاريخ النشر',
    updatedAt: 'آخر تعديل',
    reference: 'المعرّف',
    /** Where the transitions live, stated once. */
    note: 'النشر والجدولة وإلغاء النشر تقع من قائمة صفّ العنوان في قائمة المحتوى.',
    never: '—',

    /**
     * A published title whose media has not finished converting. Not a publish
     * state — the record really is منشور — but no subscriber can reach it, and
     * this card is the only place that says so.
     */
    notVisibleYet: 'غير مرئي للمستخدمين',
    notVisibleYetHint: 'ما زال التحويل جارياً؛ يظهر العنوان للمشتركين حال جاهزية أوّل ملفّ.',

    /**
     * The same fact before publication rather than after it. An unpublished
     * title with no playable rung cannot be published at all — the service
     * refuses it — and this card is where the operator stands when they go
     * looking for the reason. The note above sends them to the list for the
     * transition; without this line they arrive there to be refused.
     */
    notPublishable: 'لا يمكن النشر بعد',
    notPublishableHint: 'لم تجهز نسخة قابلة للتشغيل بعد. راجع صفحة الأصول لحالة تحويل الملفّ.',
  },

  provider: {
    heading: 'المزوّد من الخارج',
    refetch: 'أعِد جلب الدرجات',
    refetching: 'جارٍ الجلب…',
    provider: 'المزوّد',
    externalId: 'المعرّف',
    lastFetched: 'آخر جلب',
    ratingsCaption: 'درجات التقييم الخارجية بحسب مصدرها',
    ratingColumns: { source: 'المصدر', score: 'الدرجة', votes: 'عدد الأصوات' },
    /** The plurality is a data condition; one binding produces one row. */
    ratingsNote: 'المزوّد المرتبط واحد، فالجدول صفٌّ واحد. «أعِد جلب الدرجات» يحدّث الدرجة وعدد الأصوات.',
    referenceLabel: 'الرابط المرجعيّ',
    referenceText: (provider: string, externalId: string) => `الصفحة على ${provider} ${externalId}`,
    referenceNote: 'مبنيٌّ من المعرّف أعلاه، ويظهر على صفحة العنوان إلى جانب الدرجة ومصدرها.',
    unbound: 'لا ارتباط بمزوّد على هذا العنوان.',
    refetched: 'حُدِّثت الدرجات.',
  },

  cover: {
    heading: 'الغلاف',
    replace: 'استبدل الغلاف',
    current: (ratio: string) => `الغلاف الحاليّ · ${ratio}`,
    dropLabel: 'أفلت صورة الغلاف هنا',
    /**
     * The floor stated BEFORE the transfer, not after it.
     *
     * This line named the formats and stopped. The refusal that follows a file
     * under the minimum arrives at the END of the upload, so an operator who
     * was told nothing about the shape learned the requirement by failing —
     * and the sibling control on the assets page has stated ratio and floor
     * since it was written. `ratio` and `floor` arrive already read off
     * `/uploadPolicy`; both are empty when it has not loaded, and the sentence
     * then carries the formats alone rather than a floor of nothing.
     */
    hint: (formats: string, ratio: string, floor: string) =>
      ratio && floor
        ? `الصيغ المقبولة: ${formats}. أصغر مقاس مقبول ${floor} بكسل بنسبة ${ratio}.`
        : `الصيغ المقبولة: ${formats}.`,
    uploading: 'جارٍ رفع الغلاف…',
    uploaded: 'رُفع الغلاف.',
    /**
     * The cover is written onto the title by the crop worker, not by this
     * upload, so «رُفع» and «ظهر» are two different moments. The upload waits
     * out the second one; this line is only for the wait running out.
     */
    notDerived: 'رُفع الغلاف، لكنّه لم يظهر بعد. أعد المحاولة أو حدّث الصفحة لتتحقّق.',
    /** Seasons and episodes carry their own artwork, and it is managed there. */
    perAssetNote: 'وللموسم وللحلقة صورهما الخاصّة، وتُدار من صفحة الأصول.',
  },

  credits: {
    heading: 'المنسوبون',
    lede: 'الدور يُختار لكلّ عنوان على حدة — الشخص نفسه قد يُخرج عنواناً ويكتب آخر.',
  },

  actions: {
    save: 'احفظ التغييرات',
    saving: 'جارٍ الحفظ…',
    toAssets: 'الأصول والمواسم',
    toAssetsFilm: 'الأصول',
    back: 'عد إلى القائمة',
    saved: 'حُفظت التغييرات.',
  },

  states: {
    loading: 'جارٍ تحميل العنوان',
  },
} as const;
